import { Check } from 'lucide-react';
import { m } from 'framer-motion';
import { formatCurrency, t } from '../../constants/i18n';
import type { DiscountApplication } from '../../types/cartTypes';
import { DiscountLineItem } from './DiscountLineItem';

export interface DiscountSummaryPanelProps {
    totalDiscountCents: number;
    discounts: DiscountApplication[];
    currency: string;
    showDiscountCodeBadge?: boolean;
    discountCode?: string | null;
    onRemoveDiscountCode?: (code: string) => void;
    showLineItemRemove?: boolean;
    theme?: 'red' | 'green';
}

/**
 * DiscountSummaryPanel - 折扣汇总展示面板
 * 
 * 职责：纯展示组件
 * - ✅ 仅接收 Props 和回调
 * - ❌ 不引用 Service
 * - ❌ 不引用 Store
 * - ❌ 无业务逻辑
 */
export function DiscountSummaryPanel({
    totalDiscountCents,
    discounts,
    currency,
    showDiscountCodeBadge = false,
    discountCode = null,
    onRemoveDiscountCode,
    showLineItemRemove = false,
    theme = 'red'
}: DiscountSummaryPanelProps) {
    const colors = theme === 'red'
        ? { text: 'text-red-600', textDark: 'text-red-700', bg: 'bg-red-50', border: 'border-red-100' }
        : { text: 'text-green-600', textDark: 'text-green-700', bg: 'bg-green-50', border: 'border-green-100' };

    return (
        <m.div
            className="w-full overflow-hidden space-y-3"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
        >
            {/* 总折扣金额 */}
            {totalDiscountCents > 0 && (
                <div className={`flex justify-between items-center gap-2 text-sm font-semibold ${colors.text}`}>
                    <span>{t('totalSavings')}</span>
                    <span>-{formatCurrency(totalDiscountCents, currency)}</span>
                </div>
            )}

            {/* 折扣条目列表 */}
            {discounts.length > 0 && (
                <div className={`${colors.bg} p-3 rounded-xl border ${colors.border} space-y-2`}>
                    {discounts.map((discount, idx) => {
                        const discountCode = discount.title?.trim();
                        const canRemove = showLineItemRemove
                            && discount.type === 'discount_code'
                            && discountCode
                            && discountCode !== '0';

                        return (
                            <DiscountLineItem
                                key={`${discount.title}-${idx}`}
                                title={discount.title}
                                amountCents={discount.total_allocated_amount}
                                currency={currency}
                                onRemove={canRemove ? () => onRemoveDiscountCode?.(discountCode) : undefined}
                                theme={theme}
                            />
                        );
                    })}
                </div>
            )}

            {/* 优惠码标识 */}
            {showDiscountCodeBadge && discountCode && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Check className="w-5 h-5 text-green-500 shrink-0" />
                        <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-xs font-semibold text-green-700">{t('couponCodeApplied')}</span>
                            <span className="text-xs text-green-600 font-mono truncate break-all">{t('couponCode')}: {discountCode}</span>
                        </div>
                    </div>
                </div>
            )}

        </m.div>
    );
}
